/**
 * PF v2 file hub — Wave-1 kinds: power of attorney (3 upload scopes).
 * ============================================================================
 *
 * PoA has THREE upload targets across TWO endpoint families:
 *   - `POA_MAIN_KIND`  (`PF_POWER_OF_ATTORNEY`)             — the main POA doc, 1
 *   - `POA_REPS_KIND`  (`PF_POWER_OF_ATTORNEY_REPS`)        — representative POA, 1
 *   - `POA_OTHER_KIND` (`PF_POWER_OF_ATTORNEY_OTHER_FILES`) — supplementary docs, 0..15
 *
 * All three are order-level documents, so all three now mint against the v2
 * `/order-files` resource, addressed by slot `type` and carrying no
 * product reference at all. That replaces two different v1 shapes at once: the
 * dedicated PoA mints for main and reps, and — for "other" — the generic document
 * endpoint, which v1 routed through the *product* mint and then re-filed against
 * the order by overloading an `ORDER_SUPPLEMENTARY_DOCUMENT` tag. v2 rejects that
 * tag on `/files`, because the tag silently deciding which entity a file attaches
 * to is precisely what the resource split makes explicit. All three are
 * plain-upload kinds: `derivatives: []`, no derivative poll (documents have no
 * derivative artifact).
 *
 * Cardinality is per slot and matches v1 on all three (FE↔BE ledger #39, closed):
 * main and reps hold ONE document each and reject a second upload with 409, while
 * SUPPLEMENTARY holds several — the legacy "other" zone accepted 0..15, which is why
 * this scope keeps `projectMultiDoc`.
 *
 * The consequence lives in the delete, not the upload: a SUPPLEMENTARY document is
 * addressed by **slot + filename**, and addressing that slot by type alone is refused
 * with 409 rather than deleting an arbitrary member of it. All three scopes therefore
 * use the same filename-addressed delete — the backend defines it as equivalent to
 * addressing the slot for the single-document slots, so there is no per-slot branch.
 *
 * These files are deliberately NOT routed through the product mint under a different
 * tag: that would quietly change which entity they attach to, which is the ambiguity
 * the `/files` vs `/order-files` split exists to remove.
 *
 * The scopes are STATIC strings (session read live inside the resolvers) and
 * `useFileAPI`'s `INIT_SCOPE` is destructive → exactly ONE consumer per scope.
 * The composed `PfV2PoaUploadProvider` mounts all three provider owners once at
 * the PF-v2 root (`PurchaseFlowRevamp.page`) so a module reads them via the
 * `usePoa*Upload` hooks without re-firing `INIT_SCOPE`.
 *
 * A-04: these kinds are STORAGE-AGNOSTIC — they own transport + projection only.
 * The PoA MODULE owns the projection→DTO sync (interaction-gated, §4b).
 */

import { createElement, type FC, type ReactElement, type ReactNode } from 'react'

import { SessionOrderDocumentType } from 'models/purchaseFlowRevamp'

import { createFileHubKind } from '../createFileHubKind'
import { makeOrderDocumentDeleteHandler, makeOrderDocumentUploadResolver } from '../sessionFiles.transport'
import type { FileHubBaseMetadata, FileHubKind } from '../fileHub.types'
import { projectMultiDoc, projectSingleDoc, type DocProjection } from './docProjection'

// ─── Projection ────────────────────────────────────────────────────────────

/**
 * The read model every PoA scope exposes: the uploaded GCS filenames. Lifted to
 * the shared `docProjection.ts` (Wave 2 — the cert scopes reuse it); aliased
 * here so PoA's public surface (`PoaProjection`, `projectSingleDoc`,
 * `projectMultiDoc`) is byte-for-byte unchanged.
 */
export type PoaProjection = DocProjection
export { projectMultiDoc, projectSingleDoc }

// ─── Kind descriptors ────────────────────────────────────────────────────────
// An order-level document is addressed by its slot alone, so none of the three
// takes upload params any more — the "other" scope's `wireProductId` is gone with
// the product reference it existed to carry.

export const POA_MAIN_KIND: FileHubKind<FileHubBaseMetadata, PoaProjection> = {
  scope: 'PF_POWER_OF_ATTORNEY',
  derivatives: [],
  purgeOnDestroy: true,
  makeUploadResolver: (ctx) =>
    makeOrderDocumentUploadResolver(ctx, SessionOrderDocumentType.POWER_OF_ATTORNEY),
  makeDeleteHandler: (ctx) =>
    makeOrderDocumentDeleteHandler(ctx, SessionOrderDocumentType.POWER_OF_ATTORNEY),
  project: projectSingleDoc,
}

export const POA_REPS_KIND: FileHubKind<FileHubBaseMetadata, PoaProjection> = {
  scope: 'PF_POWER_OF_ATTORNEY_REPS',
  derivatives: [],
  purgeOnDestroy: true,
  makeUploadResolver: (ctx) =>
    makeOrderDocumentUploadResolver(ctx, SessionOrderDocumentType.POWER_OF_ATTORNEY_REPRESENTATIVES),
  makeDeleteHandler: (ctx) =>
    makeOrderDocumentDeleteHandler(ctx, SessionOrderDocumentType.POWER_OF_ATTORNEY_REPRESENTATIVES),
  project: projectSingleDoc,
}

/** Multi-document by design: the legacy zone accepted 0..15 supplementary files. */
export const POA_OTHER_KIND: FileHubKind<FileHubBaseMetadata, PoaProjection> = {
  scope: 'PF_POWER_OF_ATTORNEY_OTHER_FILES',
  derivatives: [],
  purgeOnDestroy: true,
  makeUploadResolver: (ctx) =>
    makeOrderDocumentUploadResolver(ctx, SessionOrderDocumentType.SUPPLEMENTARY),
  makeDeleteHandler: (ctx) =>
    makeOrderDocumentDeleteHandler(ctx, SessionOrderDocumentType.SUPPLEMENTARY),
  project: projectMultiDoc,
}

// ─── Provider + hook pairs (one per scope) ─────────────────────────────────────

const [PoaMainProvider, usePoaMainUpload] = createFileHubKind(POA_MAIN_KIND)
const [PoaRepsProvider, usePoaRepsUpload] = createFileHubKind(POA_REPS_KIND)
const [PoaOtherProvider, usePoaOtherUpload] = createFileHubKind(POA_OTHER_KIND)

export { usePoaMainUpload, usePoaRepsUpload, usePoaOtherUpload }

/**
 * The single owner of all three PoA FileAPI scopes. Mounted ONCE at the PF-v2
 * root (`PurchaseFlowRevamp.page`, wrapping `<Routes>`) next to
 * `PfV2VisualUploadProvider`, so the scopes survive step/tab switches; the
 * default `purgeOnDestroy` clears them on leaving `/purchase-flow-revamp/*`.
 * Composed by nesting the three scope providers.
 */
export const PfV2PoaUploadProvider: FC<{ children: ReactNode }> = ({ children }) =>
  [PoaMainProvider, PoaRepsProvider, PoaOtherProvider].reduceRight<ReactElement>(
    (acc, Provider) => createElement(Provider, null, acc),
    children as ReactElement
  )
